import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const FILTER_OPTIONS = ['all', 'open', 'resolved'];

const CommentItem = ({ comment, index, selected, onClick }) => {
  const resolved = comment.resolved;
  const dateStr = comment.createdAt
    ? new Date(comment.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  return (
    <Box
      onClick={() => onClick(comment.id)}
      sx={{
        px: 1.5,
        py: 1.25,
        cursor: 'pointer',
        background: selected ? '#fff7ed' : 'transparent',
        borderLeft: selected ? '3px solid #e85d26' : '3px solid transparent',
        opacity: resolved ? 0.55 : 1,
        '&:hover': { background: selected ? '#fff7ed' : '#fafafa' },
        transition: 'background 0.1s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {/* Pin number */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: resolved ? '#16a34a' : '#e85d26',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            flexShrink: 0,
            mt: 0.1,
          }}
        >
          {resolved ? <CheckCircleIcon sx={{ fontSize: 12 }} /> : index}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, color: '#333' }}>
              {comment.author || 'User'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#bbb', fontSize: 10 }}>
              {dateStr}
            </Typography>
            {(comment.replies || []).length > 0 && (
              <Chip
                icon={<ChatBubbleOutlineIcon sx={{ fontSize: 10 }} />}
                label={(comment.replies || []).length}
                size="small"
                sx={{ height: 16, fontSize: 10, ml: 'auto', '& .MuiChip-label': { px: 0.5 } }}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: 12,
              color: '#555',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textDecoration: resolved ? 'line-through' : 'none',
            }}
          >
            {comment.text}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * CommentThread — left sidebar panel listing all comments.
 *
 * Props:
 *   comments       array of comment objects
 *   selectedId     currently active comment id
 *   onSelect(id)   called when user clicks a comment row
 */
const CommentThread = ({ comments = [], selectedId, onSelect }) => {
  const [filter, setFilter] = useState('all');

  const openCount = comments.filter((c) => !c.resolved).length;

  const filtered = comments.filter((c) => {
    if (filter === 'open') return !c.resolved;
    if (filter === 'resolved') return c.resolved;
    return true;
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a' }}>
            Comments
          </Typography>
          {openCount > 0 && (
            <Chip
              label={openCount}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                background: '#e85d26',
                color: '#fff',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Box>
        <Tabs
          value={filter}
          onChange={(_, v) => setFilter(v)}
          sx={{
            minHeight: 28,
            '& .MuiTab-root': { minHeight: 28, fontSize: 11, textTransform: 'none', px: 1, py: 0 },
            '& .MuiTabs-indicator': { background: '#e85d26', height: 2 },
          }}
        >
          {FILTER_OPTIONS.map((f) => (
            <Tab key={f} value={f} label={f.charAt(0).toUpperCase() + f.slice(1)} />
          ))}
        </Tabs>
      </Box>

      <Divider />

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center', opacity: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: 12, color: '#888' }}>
              {filter === 'all'
                ? 'No comments yet.\nClick on the canvas to add one.'
                : `No ${filter} comments.`}
            </Typography>
          </Box>
        ) : (
          filtered.map((c, i) => {
            const globalIndex = comments.indexOf(c) + 1;
            return (
              <React.Fragment key={c.id}>
                <CommentItem
                  comment={c}
                  index={globalIndex}
                  selected={c.id === selectedId}
                  onClick={onSelect}
                />
                <Divider sx={{ opacity: 0.5 }} />
              </React.Fragment>
            );
          })
        )}
      </Box>

      {/* Footer hint */}
      <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #eee', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontSize: 10, color: '#bbb' }}>
          Click anywhere on the canvas to add a comment
        </Typography>
      </Box>
    </Box>
  );
};

export default CommentThread;
